import axios from 'axios';
import { getDocumentApiBase } from 'shared/api/documentUploadApi';
import { failure, interpretResponse, newCommandId } from './packingListRequest';
import { PL_SHIPMENT_TYPE_BY_TAB } from './packingListWire';

const auth0API = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;

/** 원본 업로드 API(API-07). ws 컨테이너의 HTTP 라우트라 서류 업로드와 같은 베이스 URL을 쓴다. */
export const PL_UPLOAD_PATH = '/api/v1/packing-lists/files';
/** 서버 규칙과 같은 상한·형식. 보내기 전에 화면에서 먼저 걸러 413·415를 안내한다. */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
export const UPLOAD_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.png', '.jpg', '.jpeg'];
export const UPLOAD_ACCEPT = UPLOAD_EXTENSIONS.join(',');

export const FILE_TOO_LARGE_MESSAGE = `File is too large. The limit is ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB.`;
export const UNSUPPORTED_FILE_MESSAGE = 'This file type is not supported. Upload a PDF, Excel (.xlsx, .xls), PNG or JPG file.';
const NO_RESPONSE_MESSAGE = 'The upload did not get an answer from the server. Retry — the same upload is not registered twice.';
const TOKEN_MESSAGE = 'Could not get a login token for the upload. Sign in again and retry.';

/** 거절 code별 안내. 서버 문구보다 담당자가 다음에 할 일을 알려줘야 하는 것만 바꾼다. */
const MESSAGE_BY_CODE = {
    FILE_TOO_LARGE: FILE_TOO_LARGE_MESSAGE,
    UNSUPPORTED_FILE_TYPE: UNSUPPORTED_FILE_MESSAGE,
    FILE_EMPTY: 'The file is empty.',
    FILE_REQUIRED: 'No file was received by the server.',
    COMMAND_IN_PROGRESS: 'The same upload is still being processed. Wait a moment and retry.',
    COMMAND_KEY_CONFLICT: 'This upload conflicts with an earlier one. Pick the file again to start a new upload.',
    STORAGE_UNAVAILABLE: 'File storage is unavailable. Try again shortly.',
    ACCOUNT_INACTIVE: 'Your account is inactive. Please contact your manager.',
};

/** 업로드 요청 하나의 Idempotency-Key. 파일을 고를 때 만들고, 같은 파일의 재시도는 같은 키를 다시 쓴다. */
export const newIdempotencyKey = newCommandId;

const extensionOf = (name) => {
    const match = /\.[^.]+$/.exec(String(name ?? '').trim().toLowerCase());
    return match ? match[0] : '';
};

/**
 * 보내기 전 화면에서 거르는 검사. 서버와 같은 규칙(15 MB, pdf·xlsx·xls·png·jpg)이라 통과한 파일만 올라간다.
 * 거절 모양은 서버 응답과 같아(status·code·message) 화면이 한 곳에서 안내한다.
 */
export const inspectFile = (file) => {
    if (!file) return failure('FILE_REQUIRED', 'No file selected.', 400);
    if (!UPLOAD_EXTENSIONS.includes(extensionOf(file.name))) return failure('UNSUPPORTED_FILE_TYPE', UNSUPPORTED_FILE_MESSAGE, 415);
    if (!(file.size > 0)) return failure('FILE_EMPTY', MESSAGE_BY_CODE.FILE_EMPTY, 400);
    if (file.size > MAX_UPLOAD_BYTES) return failure('FILE_TOO_LARGE', FILE_TOO_LARGE_MESSAGE, 413);
    return { ok: true, status: 200, code: 'OK', message: '', data: null, details: [] };
};

/** 담당자에게 보여줄 한 줄. */
export const describeUploadResult = (result) => MESSAGE_BY_CODE[result.code] ?? (result.message || 'Upload failed.');

/**
 * 같은 키로 다시 보내도 되는 실패인지. 응답을 못 받았거나 서버 쪽 오류(5xx)면 첫 요청이 등록됐을 수도 있는데,
 * 그래도 같은 키로 보내면 서버가 다시 만들지 않고 저장된 결과를 돌려준다 — 멱등키가 있는 이유다.
 * 413·415·400처럼 파일 자체가 거절된 경우는 파일을 바꿔야 하므로 재시도가 아니다.
 */
export const isRetryableUpload = (result) => ['NO_RESPONSE', 'COMMAND_IN_PROGRESS', 'TOKEN_UNAVAILABLE'].includes(result.code) || Number(result.status) >= 500;

/** 본문 없는 응답(프록시·ALB가 보낸 413 등)을 서버 envelope 모양으로 맞춘다. */
const bareHttpResult = (status) => {
    if (status === 413) return failure('FILE_TOO_LARGE', FILE_TOO_LARGE_MESSAGE, 413);
    if (status === 415) return failure('UNSUPPORTED_FILE_TYPE', UNSUPPORTED_FILE_MESSAGE, 415);
    return failure('HTTP_ERROR', `Upload failed (HTTP ${status}).`, status);
};

/**
 * PL 원본 한 건을 올린다. 서버는 파일을 보관하고 문서·작업 행을 만든 뒤 201 `{document_id, upload_id, version, parse_generation, file_id}`를 돌려준다.
 * 응답은 ws와 같은 envelope이라 interpretResponse로 읽는다. 파싱은 여기서 시작되지 않는다 — 이어서 ws start_parsing을 보낸다.
 *
 * @param {object} params
 * @param {File} params.file
 * @param {{tab:number, shipmentId:number, houseId:number}} params.scope
 * @param {string} params.idempotencyKey 같은 요청의 재시도는 같은 키로 보낸다.
 * @param {function} params.getAccessTokenSilently Auth0
 * @param {function} [params.onUploadProgress] (percent) => {}
 * @param {AbortSignal} [params.signal]
 */
export async function uploadPackingListFile({ file, scope, idempotencyKey, getAccessTokenSilently, onUploadProgress, signal }) {
    let token;
    try {
        token = await getAccessTokenSilently({ authorizationParams: { audience: auth0API } });
    } catch (error) {
        console.warn('Packing list upload: token refresh failed', error);
        return failure('TOKEN_UNAVAILABLE', TOKEN_MESSAGE);
    }
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('shipment_type', PL_SHIPMENT_TYPE_BY_TAB[scope.tab] ?? String(scope.tab));
    form.append('shipment_id', String(scope.shipmentId));
    form.append('house_id', String(scope.houseId));
    try {
        const resp = await axios.post(`${getDocumentApiBase()}${PL_UPLOAD_PATH}`, form, {
            headers: { Authorization: `Bearer ${token}`, 'Idempotency-Key': idempotencyKey },
            // 4xx·5xx도 본문을 읽어야 code·message로 안내할 수 있다.
            validateStatus: () => true,
            signal,
            onUploadProgress: onUploadProgress ? (e) => onUploadProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0) : undefined,
        });
        const body = resp.data && typeof resp.data === 'object' ? resp.data : null;
        return body ? interpretResponse(body) : bareHttpResult(resp.status);
    } catch (error) {
        if (axios.isCancel?.(error) || error?.name === 'CanceledError') return failure('CANCELLED', 'Upload cancelled.');
        // 응답 유실은 등록됐을 수도 있는 상태다. 문의를 추적할 수 있게 어느 요청이었는지 남긴다.
        console.warn('Packing list upload got no response', { idempotency_key: idempotencyKey, house_id: scope.houseId, filename: file?.name, error: error?.message });
        return failure('NO_RESPONSE', NO_RESPONSE_MESSAGE);
    }
}
